import type { UTCDate } from '@date-fns/utc';

import type { ScreenerReqFilters } from '@revelio/data-access';

import type {
  AllSelectionListsWithSkillTaxonomyKey,
  SelectionListKey,
} from '../../../../shared/selection-lists/types';

export type { AllSelectionListsWithSkillTaxonomyKey };

type TreeBranchLabel = { label: string; description?: string };
type TreeBranches =
  | TreeBranchLabel[]
  | ((node: { level: number; data: { name: string } }) => TreeBranchLabel);

export type AllSelectionListKeys = SelectionListKey;

// Define FilterId as a type with specific values from Selection Categories
export enum FilterId {
  COMPANY = 'COMPANY',
  INDUSTRY = 'INDUSTRY',
  GEOGRAPHY = 'GEOGRAPHY',
  ROLE = 'ROLE',
  ROLE_TAXONOMY = 'ROLE_TAXONOMY',
  SKILL_TAXONOMY = 'SKILL_TAXONOMY',
  DATE_RANGE = 'DATE_RANGE',
  POST_DATE = 'POST_DATE',
  SNAPSHOT_DATE = 'SNAPSHOT_DATE',
  GENDER = 'GENDER',
  ETHNICITY = 'ETHNICITY',
  EDUCATION = 'EDUCATION',
  SENIORITY = 'SENIORITY',
  KEYWORDS = 'KEYWORDS',
  TITLE = 'TITLE',
  DATA_ADJUSTMENTS = 'DATA_ADJUSTMENTS',
  POSTINGS_SOURCE = 'POSTINGS_SOURCE',
  METRIC_MODE = 'METRIC_MODE',
  OVERALL_RATING = 'OVERALL_RATING',
  SKILL = 'SKILL',
  CUSTOM_SKILL = 'CUSTOM_SKILL',
  CONTINGENT_WORKERS = 'CONTINGENT_WORKERS',
  GRADUATION_YEAR = 'GRADUATION_YEAR',
  PAY = 'PAY',
  EMPLOYMENT_RELATIONSHIP = 'EMPLOYMENT_RELATIONSHIP',
  WORK_SCHEDULE = 'WORK_SCHEDULE',
  FLIGHT_RISK = 'FLIGHT_RISK',
  PRESTIGE = 'PRESTIGE',
  REMOTE_SUITABILITY = 'REMOTE_SUITABILITY',
  SCHOOL = 'SCHOOL',
  NAME = 'NAME',
  MULTI_TITLE = 'MULTI_TITLE',
  MULTI_KEYWORDS = 'MULTI_KEYWORDS',
  MULTI_CUSTOM_SKILL = 'MULTI_CUSTOM_SKILL',
  MULTI_COMPANY = 'MULTI_COMPANY',
  MULTI_GEOGRAPHY = 'MULTI_GEOGRAPHY',
  MULTI_ROLE = 'MULTI_ROLE',
  MULTI_EMPLOYMENT_RELATIONSHIP = 'MULTI_EMPLOYMENT_RELATIONSHIP',
  MULTI_WORK_SCHEDULE = 'MULTI_WORK_SCHEDULE',
  MULTI_FLIGHT_RISK = 'MULTI_FLIGHT_RISK',
  MULTI_PRESTIGE = 'MULTI_PRESTIGE',
  MULTI_REMOTE_SUITABILITY = 'MULTI_REMOTE_SUITABILITY',
  MULTI_SENIORITY = 'MULTI_SENIORITY',
  MULTI_PAY = 'MULTI_PAY',
  MULTI_GENDER = 'MULTI_GENDER',
  MULTI_ETHNICITY = 'MULTI_ETHNICITY',
  MULTI_EDUCATION = 'MULTI_EDUCATION',
  MULTI_GRADUATION_YEAR = 'MULTI_GRADUATION_YEAR',
  MULTI_YEARS_OF_EXPERIENCE = 'MULTI_YEARS_OF_EXPERIENCE',
  MULTI_SCHOOL = 'MULTI_SCHOOL',
  YEARS_OF_EXPERIENCE = 'YEARS_OF_EXPERIENCE',
  // Screener-specific filters
  ATTRITION_RATE = 'ATTRITION_RATE',
  GROWTH_RATE = 'GROWTH_RATE',
  HIRING_RATE = 'HIRING_RATE',
  HEADCOUNT = 'HEADCOUNT',
  INFLOW = 'INFLOW',
  OUTFLOW = 'OUTFLOW',
  SALARY = 'SALARY',
  TENURE = 'TENURE',
  COMPETITORS = 'COMPETITORS',
  EXTERNALLY_OWNED = 'EXTERNALLY_OWNED',
  FOUNDER_INFO = 'FOUNDER_INFO',
  FOUNDER_ETHNICITY = 'FOUNDER_ETHNICITY',
  FOUNDER_GENDER = 'FOUNDER_GENDER',
  FOUNDER_COMPANY = 'FOUNDER_COMPANY',
  FOUNDER_PREVIOUS_SCHOOL = 'FOUNDER_PREVIOUS_SCHOOL',
  FUNDING_INFO = 'FUNDING_INFO',
  FUNDING_STAGE = 'FUNDING_STAGE',
  LAST_FUNDING_AMOUNT = 'LAST_FUNDING_AMOUNT',
  LAST_FUNDING_YEAR = 'LAST_FUNDING_YEAR',
  TOTAL_FUNDING_AMOUNT = 'TOTAL_FUNDING_AMOUNT',
  VALUATION = 'VALUATION',
  HQ_LOCATION = 'HQ_LOCATION',
  PUBLIC = 'PUBLIC',
  YEAR_FOUNDED = 'YEAR_FOUNDED',
  // screener multi filters
  MULTI_ATTRITION_RATE = 'MULTI_ATTRITION_RATE',
  MULTI_GROWTH_RATE = 'MULTI_GROWTH_RATE',
  MULTI_HIRING_RATE = 'MULTI_HIRING_RATE',
  MULTI_HEADCOUNT = 'MULTI_HEADCOUNT',
  MULTI_INFLOW = 'MULTI_INFLOW',
  MULTI_OUTFLOW = 'MULTI_OUTFLOW',
  MULTI_SALARY = 'MULTI_SALARY',
  MULTI_TENURE = 'MULTI_TENURE',
}

export enum LocalSelectionCategories {
  PRESTIGE = 'prestige',
  FLIGHT_RISK = 'flight_risk',
  REMOTE_SUITABILITY = 'remote_suitability',
}

export type PrimaryFilterId =
  | FilterId.COMPANY
  | FilterId.GEOGRAPHY
  | FilterId.ROLE;

export enum FilterType {
  LIST = 'list',
  RANGE = 'range',
  DATE = 'date',
  DATE_RANGE = 'date_range',
  TREE = 'tree',
  TEXT = 'text',
  VALUE = 'value',
  FREE_TEXT = 'free_text',
  CUSTOM_ROLE = 'custom_role',
  STATIC_LIST = 'static_list',
  SCHOOL = 'school',
  MULTI = 'multi',
  SEGMENTED = 'segmented',
  GROUP = 'group',
}

export type Option<T = unknown> = {
  entity?: T;
  value: string;
  label: string;
  isDisabled?: boolean;
  disabled?: boolean;
  limit?: number;
  info?: string;
};

export interface FilterSelectOptions {
  id: FilterId | 'AI_SEARCH';
  name: string;
  labelPostfix?: string;
}

export interface BaseFilter {
  id: FilterId;
  type: FilterType;
  name: string;
  isPrimary?: boolean;
  isVisible?: boolean;
  isUserSubmitted?: boolean;
  isActive?: boolean;
  children?: FilterId[];
  /**
   * Set by `applyAiSearchFilters` on values that must stay PAIRED with other
   * AI-applied filters — e.g. ROLE_TAXONOMY paired with taxonomy-relative role
   * ids. Distinct from `isUserSubmitted` (deliberately `false` on these so
   * preference inference still reads them as derived, e.g. company-adaptive
   * mode): `SyncInitialPreferencesToFilters` skips re-syncing a preference over
   * an AI-paired value, because replacing the taxonomy alone would unpair it
   * from the ids and the backend would resolve them to nothing.
   */
  isAiApplied?: boolean;
}

export interface SingleValueFilter<T> extends BaseFilter {
  type: FilterType.VALUE;
  value: T;
}

export type PrimarySelection = TreeSelection & { isActive: boolean };

export interface TreeFilter extends BaseFilter {
  type: FilterType.TREE;
  selectionListIds: AllSelectionListsWithSkillTaxonomyKey[];
  selections: TreeSelection[];
  branches?: TreeBranches;
  disabledLevels?: number[];
  isPrimary?: boolean;
  primarySelections?: PrimarySelection[];
  isCurrent?: boolean;
  isNot?: boolean;
}

export interface TreeSelection {
  id: string;
  name: string;
  levelId: AllSelectionListsWithSkillTaxonomyKey;
  isCurrent?: boolean;
}

export interface RangeFilter extends BaseFilter {
  type: FilterType.RANGE;
  min?: number;
  max?: number;
  isCurrent?: boolean; // For Pay filter to distinguish between current and previous pay
  isNot?: boolean;
}

export interface DateRangeFilter extends BaseFilter {
  type: FilterType.DATE_RANGE;
  min: UTCDate | null;
  max: UTCDate | null;
  /**
   * When true, `max` is an exact inclusive day (e.g. resolved from AI search)
   * rather than a week-picker bound. Day-index consumers (Posting Discovery)
   * must not pad it out to the end of the week.
   */
  exactEnd?: boolean;
}

export interface DateFilter extends BaseFilter {
  type: FilterType.DATE;
  value: UTCDate | null;
}

export interface ListFilter extends BaseFilter {
  type: FilterType.LIST;
  selectionListId: SelectionListKey;
  selections: ListSelection[];
  isCurrent?: boolean;
  isNot?: boolean;
}

export interface StaticListFilter extends BaseFilter {
  type: FilterType.STATIC_LIST;
  selections: ListSelection[];
  isCurrent?: boolean;
  isNot?: boolean;
}

export interface ListSelection {
  id: string;
  name: string;
  limit?: number;
}

export interface TextFilter extends BaseFilter {
  type: FilterType.TEXT;
  value: string;
}

export interface FreeTextFilter extends BaseFilter {
  type: FilterType.FREE_TEXT;
  text: string[];
  isCurrent?: boolean;
  isNot?: boolean;
}

export interface MultiFilter<T extends Filter> extends BaseFilter {
  type: FilterType.MULTI;
  wrappedFilterId: FilterId;
  instances: T[];
}

export type SchoolItem = {
  label: string;
  name: string;
  primary_name: string;
  rsid: string;
};

export interface SchoolFilter extends BaseFilter {
  type: FilterType.SCHOOL;
  schoolResultItems: Record<string, SchoolItem>;
  isNot?: boolean;
}

export interface SegmentedFilter<T extends Filter> extends BaseFilter {
  type: FilterType.SEGMENTED;
  wrappedFilter: T;
  segmentations?: Partial<Record<keyof ScreenerReqFilters, Filter>>;
}

export interface GroupFilter extends BaseFilter {
  type: FilterType.GROUP;
  children: FilterId[];
}

export type Filter =
  | TreeFilter
  | RangeFilter
  | DateRangeFilter
  | DateFilter
  | ListFilter
  | StaticListFilter
  | TextFilter
  | SingleValueFilter<unknown>
  | FreeTextFilter
  | SchoolFilter
  | GroupFilter
  | MultiFilter<FreeTextFilter>
  | MultiFilter<TreeFilter>
  | MultiFilter<RangeFilter>
  | MultiFilter<ListFilter>
  | MultiFilter<StaticListFilter>
  | MultiFilter<SchoolFilter>
  | MultiFilter<SegmentedFilter<RangeFilter>>
  | SegmentedFilter<TreeFilter>
  | SegmentedFilter<ListFilter>
  | SegmentedFilter<StaticListFilter>
  | SegmentedFilter<RangeFilter>;

export type FilterSelectionsMap = Map<FilterId, Filter>;

export interface SelectionList {
  id: string;
  name: string;
  items: ListSelection[];
}
export interface QueryContext {
  id: string;
  name: string;
}

export function isSingleValueFilter<T = unknown>(
  filter: Filter
): filter is SingleValueFilter<T> {
  return filter.type === FilterType.VALUE;
}

export function isMultiFilter(
  filter: Filter
): filter is
  | MultiFilter<FreeTextFilter>
  | MultiFilter<TreeFilter>
  | MultiFilter<RangeFilter>
  | MultiFilter<ListFilter>
  | MultiFilter<StaticListFilter>
  | MultiFilter<SchoolFilter>
  | MultiFilter<SegmentedFilter<RangeFilter>> {
  return filter.type === FilterType.MULTI;
}

export function isMultiTitleFilter(
  filter: Filter
): filter is MultiFilter<FreeTextFilter> {
  return (
    filter.type === FilterType.MULTI &&
    (filter as MultiFilter<Filter>).wrappedFilterId === FilterId.TITLE
  );
}

export function isSegmentedFilter(
  filter: Filter
): filter is
  | SegmentedFilter<TreeFilter>
  | SegmentedFilter<ListFilter>
  | SegmentedFilter<StaticListFilter>
  | SegmentedFilter<RangeFilter> {
  return filter.type === FilterType.SEGMENTED;
}

export interface MultiFilterInstanceMeta {
  parentMultiFilterId?: FilterId;
  instanceIndex?: number;
}

export type FilterWithMultiMeta = Filter & {
  parentMultiFilterId: FilterId;
  instanceIndex: number;
};

function hasProperty<T extends object, K extends PropertyKey>(
  obj: T,
  key: K
): obj is T & Record<K, unknown> {
  return key in obj;
}

export function isMultiFilterInstance(
  filter: Filter
): filter is FilterWithMultiMeta {
  if (!hasProperty(filter, 'parentMultiFilterId')) return false;
  if (!hasProperty(filter, 'instanceIndex')) return false;

  return (
    typeof filter.parentMultiFilterId !== 'undefined' &&
    typeof filter.instanceIndex !== 'undefined' &&
    filter.parentMultiFilterId !== null &&
    filter.instanceIndex !== null
  );
}
